import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Badge, Button, ColorInput, Drawer, Group, Loader, Paper, Select, Stack,
  Switch, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconClockHour4, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useBranches } from '../branches/branchesApi';
import {
  useShiftTemplates, useCreateShiftTemplate, useUpdateShiftTemplate, useDeleteShiftTemplate,
  type ShiftTemplateDto,
} from './schedulingApi';

const hhmm = (t: string) => t.slice(0, 5);
const toTimeOnly = (t: string) => (t.length === 5 ? `${t}:00` : t);

export function ShiftTemplatesPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const { data, isLoading } = useShiftTemplates();
  const { data: branches } = useBranches();
  const create = useCreateShiftTemplate();
  const update = useUpdateShiftTemplate();
  const del = useDeleteShiftTemplate();

  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState<ShiftTemplateDto | null>(null);
  const [deleting, setDeleting] = useState<ShiftTemplateDto | null>(null);

  const rows = data ?? [];
  const nameOf = (s: { nameAr: string; nameEn: string | null }) => (ar ? s.nameAr : s.nameEn ?? s.nameAr);
  const branchName = (id: string | null) => {
    if (!id) return t('hr.scheduling.templates.anyBranch');
    const b = (branches ?? []).find((x) => x.branchId === id);
    return b ? (ar ? b.nameAr : b.nameEn ?? b.nameAr) : '—';
  };
  const branchOptions = (branches ?? []).map((b) => ({ value: b.branchId, label: ar ? b.nameAr : b.nameEn ?? b.nameAr }));

  const form = useForm({
    initialValues: {
      nameAr: '', nameEn: '', branchId: null as string | null,
      startTime: '08:00', endTime: '16:00', color: '#B5191D', isActive: true,
    },
    validate: {
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      startTime: (v) => (v ? null : t('common.errors.required')),
      endTime: (v) => (v ? null : t('common.errors.required')),
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ nameAr: '', nameEn: '', branchId: null, startTime: '08:00', endTime: '16:00', color: '#B5191D', isActive: true });
    form.resetDirty();
    setDrawer(true);
  };
  const openEdit = (s: ShiftTemplateDto) => {
    setEditing(s);
    form.setValues({
      nameAr: s.nameAr, nameEn: s.nameEn ?? '', branchId: s.branchId,
      startTime: hhmm(s.startTime), endTime: hhmm(s.endTime), color: s.color ?? '#B5191D', isActive: s.isActive,
    });
    form.resetDirty();
    setDrawer(true);
  };

  const submit = form.onSubmit(async (v) => {
    const req = {
      nameAr: v.nameAr.trim(), nameEn: v.nameEn.trim() || null, branchId: v.branchId,
      startTime: toTimeOnly(v.startTime), endTime: toTimeOnly(v.endTime),
      color: v.color || null, isActive: v.isActive,
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.shiftTemplateId, req });
      else await create.mutateAsync(req);
      notifications.show({ color: 'green', title: t('common.toasts.updated'), message: v.nameAr });
      setDrawer(false);
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.hr')}
        title={t('hr.scheduling.templates.title')}
        subtitle={t('hr.scheduling.templates.subtitle')}
        actions={<Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>{t('hr.scheduling.templates.create')}</Button>}
      />
      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconClockHour4 size={22} stroke={1.6} />} title={t('hr.scheduling.templates.empty')}
            description={t('hr.scheduling.templates.emptyBody')} actionLabel={t('hr.scheduling.templates.create')} onAction={openCreate} />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('hr.scheduling.templates.name')}</Table.Th>
                <Table.Th>{t('hr.scheduling.templates.time')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('hr.scheduling.templates.hours')}</Table.Th>
                <Table.Th>{t('hr.scheduling.templates.branch')}</Table.Th>
                <Table.Th>{t('common.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((s) => (
                <Table.Tr key={s.shiftTemplateId}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color ?? 'var(--wheels-border)', display: 'inline-block' }} />
                      <Text fw={500} c="var(--wheels-ink-strong)">{nameOf(s)}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><span className="font-numeric">{hhmm(s.startTime)} – {hhmm(s.endTime)}</span></Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{s.hours}</Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{branchName(s.branchId)}</Table.Td>
                  <Table.Td>
                    {s.isActive
                      ? <Badge color="green" variant="light" radius="sm">{t('common.active')}</Badge>
                      : <Badge color="gray" variant="light" radius="sm">{t('common.inactive')}</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(s)} aria-label={t('common.edit')}><IconEdit size={16} stroke={1.6} /></ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon variant="subtle" color="red" onClick={() => setDeleting(s)} aria-label={t('common.delete')}><IconTrash size={16} stroke={1.6} /></ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer opened={drawer} onClose={() => setDrawer(false)} position={ar ? 'left' : 'right'}
        size="md" padding="lg" title={editing ? t('hr.scheduling.templates.editTitle') : t('hr.scheduling.templates.create')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <TextInput label={t('hr.scheduling.templates.nameAr')} withAsterisk {...form.getInputProps('nameAr')} />
            <TextInput label={t('hr.scheduling.templates.nameEn')} {...form.getInputProps('nameEn')} />
            <Group grow>
              <TimeInput label={t('hr.scheduling.templates.startTime')} withAsterisk {...form.getInputProps('startTime')} />
              <TimeInput label={t('hr.scheduling.templates.endTime')} withAsterisk {...form.getInputProps('endTime')} />
            </Group>
            <Select label={t('hr.scheduling.templates.branch')} data={branchOptions} clearable searchable
              placeholder={t('hr.scheduling.templates.anyBranch')} {...form.getInputProps('branchId')} />
            <ColorInput label={t('hr.scheduling.templates.color')} format="hex" {...form.getInputProps('color')} />
            {editing && (
              <Switch label={t('common.active')} checked={form.values.isActive}
                onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)} />
            )}
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={create.isPending || update.isPending}>{editing ? t('common.save') : t('common.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      <ConfirmDialog opened={deleting !== null} onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try { await del.mutateAsync(deleting.shiftTemplateId); setDeleting(null); }
          catch (err) { notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody') }); }
        }}
        title={t('hr.scheduling.templates.deleteTitle')} message={t('hr.scheduling.templates.deleteBody', { name: deleting ? nameOf(deleting) : '' })}
        loading={del.isPending} />
    </Stack>
  );
}
